import { useSelector } from 'react-redux'
import { userSlice } from '../../../slices/userSlice'
import {
  useGetApiV1UsersRelationshipsQuery,
  useLazyGetApiV1UsersRelationshipsQuery,
} from '../../../userApi'
import useUserChange from 'hooks/useUserChange'

type HookProps = {
  userId?: string | string[]
  skip?: boolean
}

type GetSafeListProps = HookProps & {
  currentUserId?: string
}

const FILTERS = [
  'following',
  'followed',
  'blocked',
  'muted',
  'note',
  'blocked_by',
]

const getHookProps = ({ userId, currentUserId }: GetSafeListProps) => {
  const isSingleUser = typeof userId === 'string'
  const safeTargets = isSingleUser
    ? [userId]
    : userId?.filter((id) => id !== currentUserId) || []

  const isMyUser = userId === currentUserId
  const isEmpty = !safeTargets.length
  const canFetch = !!(!isEmpty && !isMyUser && currentUserId)

  return { isSingleUser, canFetch, safeTargets }
}

export const useRelationship = ({ userId, skip }: HookProps) => {
  const currentUser = useSelector(userSlice.selectors.selectCurrentUser)
  const currentUserId = currentUser?.id

  const { canFetch, isSingleUser, safeTargets } = getHookProps({
    userId,
    currentUserId,
  })

  const { data, isLoading } = useGetApiV1UsersRelationshipsQuery(
    {
      targets: safeTargets,
      filter: FILTERS,
    },
    {
      skip: !canFetch || skip,
    },
  )

  const [updateRelationship] = useLazyGetApiV1UsersRelationshipsQuery()

  const updateRelations = ({ userId }: HookProps) => {
    const { canFetch, safeTargets } = getHookProps({
      userId,
      currentUserId,
    })

    if (!canFetch) return

    void updateRelationship({
      targets: safeTargets,
      filter: FILTERS,
    })
  }

  useUserChange({
    onChange: () => void updateRelations({ userId: safeTargets }),
  })

  const relationship = canFetch
    ? (isSingleUser ? data?.[userId as string] : data) || {}
    : {}

  return {
    relationship,
    isLoading: canFetch && isLoading,
    updateRelations,
  }
}
